'use client';

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { FOCUS_RING_BRAND_SHADOW_SM } from '../styles/common';
import { cn } from '../utils';

// Create context for tooltip state
type TooltipContextType = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const TooltipContext = React.createContext<TooltipContextType | undefined>(
  undefined,
);

const TooltipProvider = TooltipPrimitive.Provider;

// Modified Tooltip to provide context
function Tooltip(
  props: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>,
) {
  const [open, setOpen] = React.useState(false);

  const contextValue = React.useMemo(() => ({ open, setOpen }), [open]);

  return (
    <TooltipContext.Provider value={contextValue}>
      <TooltipPrimitive.Root open={open} onOpenChange={setOpen} {...props} />
    </TooltipContext.Provider>
  );
}
Tooltip.displayName = 'Tooltip';

// Hook to use tooltip context
function useTooltip() {
  const context = React.useContext(TooltipContext);
  if (!context) {
    throw new Error('Tooltip components must be used within a Tooltip');
  }
  return context;
}

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger> & {
    disableClickTrigger?: boolean;
  }
>(({ onClick, className, disableClickTrigger, ...props }, ref) => {
  const { open, setOpen } = useTooltip();

  function handleClick(e: React.MouseEvent<HTMLButtonElement>) {
    if (disableClickTrigger) {
      return;
    }
    e.preventDefault();
    if (!open) {
      setOpen(true);
    }
    onClick?.(e);
  }

  return (
    <TooltipPrimitive.Trigger
      type="button"
      onClick={handleClick}
      className={cn(FOCUS_RING_BRAND_SHADOW_SM, className)}
      {...props}
      ref={ref}
    />
  );
});
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, children, ...props }, ref) => (
  <TooltipPrimitive.Content
    className={cn(
      'z-50 overflow-hidden rounded-md border bg-white p-3 text-sm text-gray-700 shadow-lg dark:border-gray-800 dark:bg-gray-950 dark:text-gray-50',
      // Animation
      'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      className,
    )}
    ref={ref}
    sideOffset={sideOffset}
    {...props}
  >
    {children}
  </TooltipPrimitive.Content>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
